// src/components/ContextualOnboarding.jsx
import React from "react";
import { Popover, Box, Typography, Button, Stack, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import useContextualOnboarding from "../hooks/useContextualOnboarding";

/**
 * Onboarding contextuel générique, non bloquant, ancré à un élément de la page.
 *
 * Usage :
 *   const targetRef = useRef(null);
 *   <ContextualOnboarding
 *     pageKey="reservations"
 *     steps={[
 *       { title: "...", text: "...", targetRef, placement: "bottom" },
 *       ...
 *     ]}
 *   />
 *
 * Comportement :
 * - Ne s'affiche que si l'onboarding n'a jamais été marqué comme vu (localStorage).
 * - Si la ref de l'étape courante ne pointe vers aucun élément monté, l'étape est
 *   automatiquement sautée (jamais de blocage, jamais d'erreur visible).
 * - Fermeture (croix) ou "Passer" marquent l'onboarding comme vu définitivement.
 * - "Terminer" (dernière étape) marque également comme vu.
 * - Aucun blocage de l'interface sous-jacente : ce n'est jamais un Dialog modal.
 */
export default function ContextualOnboarding({ pageKey, steps, autoStart = true }) {
  const { t } = useTranslation();
  const { shouldShow, markSeen } = useContextualOnboarding(pageKey);

  const [stepIndex, setStepIndex] = React.useState(0);
  const [active, setActive] = React.useState(autoStart);

  const validSteps = React.useMemo(
    () => (Array.isArray(steps) ? steps : []),
    [steps]
  );

  // Trouve le prochain index dont la ref cible est réellement montée (ou sans ref = toujours affichable)
  const findNextValidIndex = React.useCallback(
    (fromIndex, direction = 1) => {
      let i = fromIndex;
      while (i >= 0 && i < validSteps.length) {
        const step = validSteps[i];
        const hasRef = !!step.targetRef;
        const el = hasRef ? step.targetRef.current : true;
        if (el) return i;
        i += direction;
      }
      return -1;
    },
    [validSteps]
  );

  // Ref stable vers la derniere version de findNextValidIndex, pour eviter que
  // l'effet d'initialisation ne se redeclenche a chaque nouvelle reference de
  // callback (elle-meme causee par un simple re-render du parent recreant le
  // tableau `steps`). L'effet ne doit reagir qu'a de vrais changements d'etat.
  const findNextValidIndexRef = React.useRef(findNextValidIndex);
  findNextValidIndexRef.current = findNextValidIndex;

  React.useEffect(() => {
    if (!shouldShow || !active || validSteps.length === 0) return;
    const firstValid = findNextValidIndexRef.current(0, 1);
    if (firstValid === -1) {
      // Aucune ancre disponible pour aucune étape : on ne bloque jamais, on abandonne silencieusement.
      return;
    }
    setStepIndex(firstValid);
    // eslint-disable-next-line
  }, [shouldShow, active, validSteps.length]);

  if (!shouldShow || !active || validSteps.length === 0) return null;

  const currentStep = validSteps[stepIndex];
  if (!currentStep) return null;

  const anchorEl = currentStep.targetRef ? currentStep.targetRef.current : null;
  if (currentStep.targetRef && !anchorEl) {
    // Cible pas encore montée : on n'affiche rien pour cette étape, sans planter.
    return null;
  }

  const isLastStep = stepIndex >= validSteps.length - 1;
  const isFirstStep = stepIndex <= 0;

  const handleClose = () => {
    markSeen();
    setActive(false);
  };

  const handleNext = () => {
    if (isLastStep) {
      handleClose();
      return;
    }
    const next = findNextValidIndex(stepIndex + 1, 1);
    if (next === -1) {
      handleClose();
      return;
    }
    setStepIndex(next);
  };

  const handlePrev = () => {
    if (isFirstStep) return;
    const prev = findNextValidIndex(stepIndex - 1, -1);
    if (prev === -1) return;
    setStepIndex(prev);
  };

  return (
    <Popover
      open
      anchorEl={anchorEl}
      onClose={handleClose}
      anchorOrigin={{
        vertical: currentStep.placement === "top" ? "top" : "bottom",
        horizontal: "center",
      }}
      transformOrigin={{
        vertical: currentStep.placement === "top" ? "bottom" : "top",
        horizontal: "center",
      }}
      disableRestoreFocus
      sx={{ pointerEvents: "none" }}
      slotProps={{ paper: { sx: { pointerEvents: "auto" } } }}
    >
      <Box sx={{ p: 2, maxWidth: 320 }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
          <Typography variant="subtitle2" fontWeight={700}>
            {currentStep.title}
          </Typography>
          <IconButton size="small" onClick={handleClose} sx={{ mt: -0.5, mr: -0.5 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
          {currentStep.text}
        </Typography>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Button size="small" onClick={handleClose} sx={{ textTransform: "none" }}>
            {t("onboarding.skip", { defaultValue: "Passer" })}
          </Button>
          <Stack direction="row" spacing={1}>
            {!isFirstStep && (
              <Button size="small" onClick={handlePrev} sx={{ textTransform: "none" }}>
                {t("onboarding.previous", { defaultValue: "Précédent" })}
              </Button>
            )}
            <Button size="small" variant="contained" onClick={handleNext} sx={{ textTransform: "none", borderRadius: 2 }}>
              {isLastStep
                ? t("onboarding.finish", { defaultValue: "Terminer" })
                : t("onboarding.next", { defaultValue: "Suivant" })}
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Popover>
  );
}
